import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Envelope } from './lib/api';
import { useAuth } from './lib/auth';
import type { Dashboard } from './lib/types';
import { useBranchDashboard } from './lib/use-branch-dashboard';
import { AppShell, alertsFrom, branchAlertsFrom } from './components/app-shell';
import { EmptyState } from './components/empty-state';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/login';
import { DashboardPage } from './pages/dashboard';
import { BranchDashboardPage } from './pages/branch-dashboard';
import { ItemsPage } from './pages/items';
import { ItemDetailPage } from './pages/item-detail';
import { WarehousePage } from './pages/warehouse';
import { BranchesPage } from './pages/branches';
import { CreditPage } from './pages/credit';
import { InstallmentsPage } from './pages/installments';
import { PurchasingPage } from './pages/purchasing';
import { PurchaseDetailPage } from './pages/purchase-detail';
import { OrdersPage } from './pages/orders';
import { SalesPage } from './pages/sales';
import { TransfersPage } from './pages/transfers';
import { ReceivingPage } from './pages/receiving';
import { StockCountsPage } from './pages/stock-counts';
import { WarrantyPage } from './pages/warranty';
import { FinancePage } from './pages/finance';
import { WholesalePage } from './pages/wholesale';
import { StatsPage } from './pages/stats';
import { AccountingPage } from './pages/accounting';
import { ReturnsPage } from './pages/returns';
import { ReportsPage } from './pages/reports';
import { CalendarPage } from './pages/calendar';
import { ReferencePage } from './pages/reference';
import { SettingsPage } from './pages/settings';
import { SetupSuppliersPage } from './pages/setup/suppliers';
import { SetupStoresPage } from './pages/setup/stores';
import { SetupStaffPage } from './pages/setup/staff';

export function App() {
  const { user, loading } = useAuth();

  // Эхний refresh дуусах хүртэл юу ч харуулахгүй — нэвтрэх дэлгэц анивчихаас сэргийлнэ
  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Сесс шалгаж байна…" />
      </div>
    );
  }

  if (!user) return <LoginPage />;
  return <AuthedApp />;
}

function AuthedApp() {
  const { hasPerm } = useAuth();
  /*
    Компанийн тайлан харах эрхтэй бол цэсний тоо ба мэдэгдэл компанийн
    самбараас; эс бөгөөс (салбарын ажилтан) өөрийн салбарын самбараас —
    дууссан размер, 0₮ гутал, замд яваа шилжүүлэг.

    ⚠️ Эрхгүй хүсэлтийг ИЛГЭЭХГҮЙ (`enabled`): салбарын ажилтанд
       `/reports/dashboard` 403 өгнө, хуудас бүрт дэмий алдаа үлдэнэ.
  */
  const company = hasPerm('dashboard.view') || hasPerm('reports.view');
  // Түлхүүр нь дашбоардын анхдагчтай ижил тул тэр хуудсан дээр давхар хүсэлт явахгүй
  const { data } = useQuery({
    queryKey: ['dashboard', 'month'],
    queryFn: () => api<Envelope<Dashboard>>('/reports/dashboard?period=month'),
    staleTime: 60_000,
    enabled: company,
  });
  const branch = useBranchDashboard(undefined, !company && hasPerm('branchDashboard.view'));

  const d = data?.data;

  return (
    <Routes>
      <Route
        element={
          <AppShell
            counts={{
              purchases: d?.nav.purchases,
              items: d?.nav.items,
              orders: d?.nav.orders,
              stores: d?.nav.stores,
              staff: d?.nav.staff,
            }}
            alerts={company ? alertsFrom(d) : branchAlertsFrom(branch.data?.data)}
          />
        }
      >
        <Route index element={<HomePage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="items/:id" element={<ItemDetailPage />} />        <Route path="warehouse" element={<WarehousePage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="purchasing" element={<PurchasingPage />} />
        <Route path="credit" element={<CreditPage />} />
        <Route path="installments" element={<InstallmentsPage />} />
        <Route path="purchasing/:id" element={<PurchaseDetailPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="sales" element={<SalesPage />} />
        <Route path="transfers" element={<TransfersPage />} />
        <Route path="receiving" element={<ReceivingPage />} />
        <Route path="stock-counts" element={<StockCountsPage />} />
        <Route path="warranty" element={<WarrantyPage />} />
        <Route path="returns" element={<ReturnsPage />} />
        <Route path="finance" element={<FinancePage />} />
        <Route path="wholesale" element={<WholesalePage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="stats" element={<StatsPage />} />
        <Route path="accounting" element={<AccountingPage />} />

        {/* ─── Тохиргоо: мөр ҮҮСГЭХ, ЗАСАХ бүхэн энд ───
            Үйл ажиллагааны хуудсууд (/purchasing, /branches)
            зөвхөн ХАРУУЛНА. Хоёрыг нэг хуудсанд хольсноос болж ажилтан
            нийлүүлэгч, салбар, хэрэглэгч нэмэх газраа олдоггүй байв. */}
        <Route path="setup/suppliers" element={<SetupSuppliersPage />} />
        <Route path="setup/stores" element={<SetupStoresPage />} />
        <Route path="setup/staff" element={<SetupStaffPage />} />
        <Route path="setup/reference" element={<ReferencePage />} />
        <Route path="setup/system" element={<SettingsPage />} />

        {/* Хуучин хавчуурга эвдрэхгүй */}
        <Route path="reference" element={<Navigate to="/setup/reference" replace />} />
        <Route path="settings" element={<Navigate to="/setup/system" replace />} />
        {/* Ажилтны хуудас хасагдсан (2026-10-05) — бүртгэл нь «Тохиргоо → Ажилтан» */}
        <Route path="staff" element={<Navigate to="/setup/staff" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * `/` — эрхээр сонгоно. Компанийн самбар давамгайлна: АДМИН бүх эрхтэй
 * тул салбарын самбар руу унавал компанийн тоогоо алдана.
 *
 * ⚠️ Хоёулаа хаалттай бол (админ эрх хассан) 403-ын алдааны карт биш,
 *    юу хийхийг хэлнэ.
 */
function HomePage() {
  const { hasPerm } = useAuth();
  if (hasPerm('dashboard.view')) return <DashboardPage />;
  if (hasPerm('branchDashboard.view')) return <BranchDashboardPage />;
  // Нягтлан бодогчийн нүүр — тайлангийн хуудас (2026-10-15)
  if (hasPerm('accounting.view')) return <AccountingPage />;
  return (
    <div className="rounded-card border border-hair bg-panel">
      <EmptyState
        title="Самбар харах эрх алга"
        description="Зүүн талын цэснээс ажлаа сонгоно уу. Самбар хэрэгтэй бол админд хандана уу."
      />
    </div>
  );
}
